import { AuthRefreshResponse } from "@/api/generated-api";
import Cookies from "js-cookie";

const ACCESS_TOKEN_COOKIE = "access_token";
const REFRESH_TOKEN_COOKIE = "refresh_token";
const AUTH_STATE_CHANGE_EVENT = "auth-state-change";

export interface ApiAuthTokens extends Partial<AuthRefreshResponse> {}

const isBrowser = () => typeof document !== "undefined";

const readCookie = (name: string): string | null => (isBrowser() ? (Cookies.get(name) ?? null) : null);

const writeCookie = (name: string, value: string) => {
  if (!isBrowser()) return;

  Cookies.set(name, value, {
    sameSite: "lax",
    path: "/",
  });
};

const removeCookie = (name: string) => {
  if (!isBrowser()) return;

  Cookies.remove(name, { path: "/" });
};

const emitAuthStateChange = () => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
};

export const getAccessToken = (): string | null => readCookie(ACCESS_TOKEN_COOKIE);

export const getRefreshToken = (): string | null => readCookie(REFRESH_TOKEN_COOKIE);

export const subscribeToAuthStateChange = (listener: () => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  window.addEventListener(AUTH_STATE_CHANGE_EVENT, listener);

  return () => {
    window.removeEventListener(AUTH_STATE_CHANGE_EVENT, listener);
  };
};

export const setAuthTokens = (accessToken: string, refreshToken: string) => {
  writeCookie(ACCESS_TOKEN_COOKIE, accessToken);
  writeCookie(REFRESH_TOKEN_COOKIE, refreshToken);
  emitAuthStateChange();
};

export const clearAuthTokens = async () => {
  if (isBrowser()) {
    removeCookie(ACCESS_TOKEN_COOKIE);
    removeCookie(REFRESH_TOKEN_COOKIE);
    emitAuthStateChange();
    return;
  }

  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();

    cookieStore.delete(ACCESS_TOKEN_COOKIE);
    cookieStore.delete(REFRESH_TOKEN_COOKIE);
  } catch {
    // Cookies can be read-only in some server contexts.
  }
};

export const getServerApiAuth = async (): Promise<ApiAuthTokens> => {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();

  return {
    access_token: cookieStore.get(ACCESS_TOKEN_COOKIE)?.value,
    refresh_token: cookieStore.get(REFRESH_TOKEN_COOKIE)?.value,
  };
};
